export function normalizeTriviaLocale(locale) {
  const normalized = String(locale || '').trim().toLowerCase().replace(/_/g, '-');
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
  return 'es';
}

export function resolveTriviaLocalizedValue(value, locale, legacyValue = '') {
  const requested = normalizeTriviaLocale(locale);

  if (typeof value === 'string' && value.trim()) return value;

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const requestedValue = value[requested];
    if (typeof requestedValue === 'string' && requestedValue.trim()) return requestedValue;

    if (typeof value.es === 'string' && value.es.trim()) return value.es;

    const available = Object.values(value).find(
      (candidate) => typeof candidate === 'string' && candidate.trim()
    );
    if (available) return available;
  }

  return typeof legacyValue === 'string' ? legacyValue : '';
}

export function triviaAnswerId(answer, index) {
  const explicit = String(answer?.id || '').trim();
  return explicit || `option_${index}`;
}

export function isTriviaAnswerCorrect(question, answer, index) {
  const correctOptionId = String(question?.correctOptionId || '').trim();
  if (correctOptionId) {
    return triviaAnswerId(answer, index) === correctOptionId;
  }
  return answer?.correct === true;
}

export function normalizeTriviaQuestion(question) {
  if (!question || typeof question !== 'object') return null;

  const localized = question.localized && typeof question.localized === 'object'
    ? question.localized
    : {};
  const legacyText = typeof question.text === 'string' ? question.text.trim() : '';
  const text = localized.text || question.question || question.text;
  const spanishText = resolveTriviaLocalizedValue(text, 'es', legacyText).trim();
  if (!spanishText) return null;

  const rawAnswers = Array.isArray(question.answers)
    ? question.answers
    : (Array.isArray(question.options) ? question.options : []);

  const answers = rawAnswers
    .filter((answer) => answer && typeof answer === 'object')
    .map((answer, index) => {
      const answerLocalized = answer.label || answer.localized || answer.text;
      const legacyAnswerText = typeof answer.text === 'string' ? answer.text.trim() : '';
      const spanishLabel = resolveTriviaLocalizedValue(answerLocalized, 'es', legacyAnswerText).trim();
      if (!spanishLabel) return null;

      return {
        ...answer,
        id: triviaAnswerId(answer, index),
        text: legacyAnswerText || spanishLabel,
        label: answerLocalized && typeof answerLocalized === 'object'
          ? { ...answerLocalized }
          : { es: spanishLabel },
        correct: answer.correct === true,
      };
    })
    .filter(Boolean);

  if (answers.length < 2) return null;

  const explicitCorrectOptionId = String(question.correctOptionId || '').trim();
  const legacyCorrectAnswer = typeof question.correctAnswer === 'string'
    ? question.correctAnswer.trim()
    : '';
  const inferredCorrect = answers.find((answer) => (
    answer.correct === true ||
    (legacyCorrectAnswer && answer.text === legacyCorrectAnswer)
  ));
  const correctOptionId = explicitCorrectOptionId || inferredCorrect?.id || '';
  if (!correctOptionId || !answers.some((answer) => answer.id === correctOptionId)) return null;

  const explanation = localized.explanation || question.explanation;

  return {
    ...question,
    id: String(question.id || '').trim(),
    category: question.category || 'general',
    difficulty: question.difficulty || 'easy',
    text: legacyText || spanishText,
    localized: {
      ...localized,
      text: text && typeof text === 'object' ? { ...text } : { es: spanishText },
      explanation: explanation && typeof explanation === 'object'
        ? { ...explanation }
        : (typeof explanation === 'string' && explanation.trim()
          ? { es: explanation.trim() }
          : {}),
    },
    answers,
    correctOptionId,
    explanation: typeof question.explanation === 'string' ? question.explanation : null,
    image: question.image || null,
  };
}

export function resolveTriviaQuestion(question, locale) {
  const normalized = normalizeTriviaQuestion(question);
  if (!normalized) return null;

  return {
    ...normalized,
    text: resolveTriviaLocalizedValue(
      normalized.localized?.text,
      locale,
      normalized.text
    ),
    answers: normalized.answers.map((answer, index) => ({
      ...answer,
      text: resolveTriviaLocalizedValue(answer.label, locale, answer.text),
      correct: isTriviaAnswerCorrect(normalized, answer, index),
    })),
    explanation: resolveTriviaLocalizedValue(
      normalized.localized?.explanation,
      locale,
      normalized.explanation || ''
    ) || null,
  };
}
